import type { Platform } from '../context/env-context';
import z, { type ZodType } from '../zod';

export class ApiResponseError<TData = unknown> extends Error {
  public readonly status: number;
  public readonly data: TData;

  public static errorSchemas = {
    errors: z.object({
      errors: z.array(
        z.object({
          code: z.string().optional(),
          message: z.string().optional(),
          property: z.string().optional(),
        }),
      ),
    }),
    detail: z.object({ detail: z.string().optional() }),
    array: z.array(
      z.object({
        line: z.number().optional(),
        field: z.string().optional(),
        message: z.string(),
      }),
    ),
    message: z.object({ message: z.string().optional() }),
    validation: z.object({
      message: z.string().optional(),
      errors: z.record(z.string(), z.array(z.string())),
    }),
  };

  constructor(status: number, data: TData, message?: string) {
    super(message ?? `Request failed with status code ${status}`);
    this.name = 'ApiResponseError';
    this.status = status;
    this.data = data;
    Object.setPrototypeOf(this, ApiResponseError.prototype);
  }

  static isApiResponseError<TData = unknown>(
    err: unknown,
    expectedDataShape: ZodType<TData>,
  ): err is ApiResponseError<TData> {
    if (!(err instanceof ApiResponseError)) {
      return false;
    }

    return expectedDataShape.safeParse(err.data).success;
  }
}

/** True when `err` is an `ApiResponseError` carrying exactly `status`. */
export function isApiErrorStatus(err: unknown, status: number): boolean {
  return err instanceof ApiResponseError && err.status === status;
}

/**
 * Flattened, de-duplicated messages from a Laravel-style validation error
 * (`{ message?, errors: Record<string, string[]> }`), or `null` if `err` isn't
 * one. `message` and the per-field `errors` can repeat the same string.
 */
export function validationMessages(err: unknown): string[] | null {
  if (!ApiResponseError.isApiResponseError(err, ApiResponseError.errorSchemas.validation)) {
    return null;
  }
  const { data } = err;
  const messages = data.message ? [data.message] : [];
  for (const fieldMessages of Object.values(data.errors)) {
    messages.push(...fieldMessages);
  }
  return Array.from(new Set(messages));
}

export class NavigationError extends Error {
  constructor(message?: string) {
    super(message ?? 'Navigation error');
    this.name = 'NavigationError';
    Object.setPrototypeOf(this, NavigationError.prototype);
  }
}

export class NavigationPathIdNotResolvedError extends NavigationError {
  constructor(
    public readonly pathId: string,
    public readonly platform?: Platform,
  ) {
    super('Navigation error, path not found');
    this.name = 'NavigationPathIdNotResolvedError';
    Object.setPrototypeOf(this, NavigationPathIdNotResolvedError.prototype);
  }
}

export type ErrorMessage = { message: string };
export type WithFormField<T extends string> = 'form' | T;

export type FieldError<T extends string = string> = { field: WithFormField<T> } & ErrorMessage;

export type ErrorResolver<TField extends string = string> = (error: unknown) => FieldError<TField>;

// types for resolving errors with fields and messages
export type FormErrorResolver = (error: unknown) =>
  | {
      form: ErrorMessage;
    }
  | {
      fields: Record<string, ErrorMessage>;
    };
